import { useState, useEffect, useCallback } from "react";
import { BadgeCheck, Clock, XCircle } from "lucide-react";
import { RubyIcon } from "@/components/Icons";
import { toast } from "sonner";
import api, { errText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function EarningsTab() {
  const { setUser } = useAuth();
  const [data, setData] = useState(null);
  const [kycForm, setKycForm] = useState({ full_name: "", pan: "", bank_account: "", ifsc: "", upi: "" });
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/payouts/me").then(({ data: d }) => setData(d)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const submitKyc = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/payouts/kyc", kycForm);
      toast.success("KYC submitted for review");
      load();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const requestPayout = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/payouts/request", { diamonds: Number(amount) });
      toast.success(`Payout requested — ₹${res.payout.net_inr} after review`);
      setUser((u) => ({ ...u, diamonds: u.diamonds - Number(amount) }));
      setAmount("");
      load();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <div className="space-y-3">{[1, 2].map((i) => <div key={i} className="skeleton h-24 rounded-2xl" />)}</div>;

  const { kyc, config } = data;
  const net = amount ? ((Number(amount) / config.diamonds_per_inr) * (1 - config.payout_commission / 100)).toFixed(0) : 0;
  const statusUi = {
    pending: { icon: Clock, color: "text-amber-400", label: "KYC under review" },
    verified: { icon: BadgeCheck, color: "text-emerald-400", label: "KYC verified" },
    rejected: { icon: XCircle, color: "text-rose-400", label: `KYC rejected${kyc?.note ? ` — ${kyc.note}` : ""}` },
  };

  return (
    <div data-testid="earnings-tab">
      <div className="glass rounded-2xl p-4 mb-4 flex items-center justify-between">
        <div>
          <p className="text-zinc-400 text-xs font-semibold">Ruby earnings</p>
          <p className="text-2xl font-extrabold text-rose-300 flex items-center gap-1.5"><RubyIcon size={22} /> {data.diamonds.toLocaleString()}</p>
        </div>
        <p className="text-[10px] text-zinc-500 text-right">≈ ₹{Math.floor(data.diamonds / config.diamonds_per_inr).toLocaleString()} gross<br />{config.payout_commission}% platform fee applies</p>
      </div>

      {kyc && (
        <div className={`glass rounded-2xl px-4 py-3 mb-4 flex items-center gap-2`} data-testid="kyc-status">
          {(() => { const S = statusUi[kyc.status]; const I = S.icon; return <><I size={16} className={S.color} /><p className={`text-xs font-semibold ${S.color}`}>{S.label}</p></>; })()}
        </div>
      )}

      {(!kyc || kyc.status === "rejected") && (
        <form onSubmit={submitKyc} className="glass rounded-2xl p-4 mb-4 space-y-2.5" data-testid="kyc-form">
          <p className="text-xs font-bold text-zinc-200">Complete KYC to withdraw</p>
          <input data-testid="kyc-name-input" required placeholder="Full legal name" value={kycForm.full_name}
            onChange={(e) => setKycForm((f) => ({ ...f, full_name: e.target.value }))}
            className="w-full bg-[#131A2A] border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-violet-500" />
          <input data-testid="kyc-pan-input" required placeholder="PAN (ABCDE1234F)" value={kycForm.pan}
            onChange={(e) => setKycForm((f) => ({ ...f, pan: e.target.value.toUpperCase() }))}
            className="w-full bg-[#131A2A] border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-violet-500" />
          <input data-testid="kyc-upi-input" placeholder="UPI ID (or fill bank below)" value={kycForm.upi}
            onChange={(e) => setKycForm((f) => ({ ...f, upi: e.target.value }))}
            className="w-full bg-[#131A2A] border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-violet-500" />
          <div className="flex gap-2">
            <input data-testid="kyc-bank-input" placeholder="Bank account no." value={kycForm.bank_account}
              onChange={(e) => setKycForm((f) => ({ ...f, bank_account: e.target.value }))}
              className="flex-1 bg-[#131A2A] border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-violet-500" />
            <input data-testid="kyc-ifsc-input" placeholder="IFSC" value={kycForm.ifsc}
              onChange={(e) => setKycForm((f) => ({ ...f, ifsc: e.target.value.toUpperCase() }))}
              className="w-28 bg-[#131A2A] border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-violet-500" />
          </div>
          <button data-testid="kyc-submit-button" disabled={busy} className="w-full gradient-primary text-white font-bold py-3 rounded-xl disabled:opacity-40">
            {busy ? "Submitting…" : "Submit KYC"}
          </button>
        </form>
      )}

      {kyc?.status === "verified" && (
        <div className="glass rounded-2xl p-4 mb-4" data-testid="payout-request-card">
          <p className="text-xs font-bold text-zinc-200 mb-2">Request payout (min {config.min_payout_diamonds.toLocaleString()} <RubyIcon size={11} className="align-[-1px]" />)</p>
          <div className="flex gap-2">
            <input data-testid="payout-amount-input" type="number" min={config.min_payout_diamonds} max={data.diamonds}
              placeholder="Rubies" value={amount} onChange={(e) => setAmount(e.target.value)}
              className="flex-1 bg-[#131A2A] border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-violet-500" />
            <button data-testid="payout-request-button" onClick={requestPayout} disabled={!amount || busy}
              className="gradient-primary text-white text-xs font-bold px-4 rounded-xl disabled:opacity-40">
              Withdraw{amount ? ` ₹${net}` : ""}
            </button>
          </div>
        </div>
      )}

      <p className="text-xs font-bold text-zinc-200 mb-2">Payout history</p>
      <div className="space-y-1.5" data-testid="payout-history">
        {data.payouts.length === 0 && <p className="text-zinc-500 text-xs text-center py-4">No payouts yet</p>}
        {data.payouts.map((p) => (
          <div key={p.id} className="flex items-center justify-between bg-[#131A2A] rounded-xl px-3.5 py-2.5">
            <div>
              <p className="text-xs text-white font-semibold flex items-center gap-1"><RubyIcon size={11} /> {p.diamonds.toLocaleString()} → ₹{p.net_inr.toLocaleString()}</p>
              <p className="text-[9px] text-zinc-500">{new Date(p.created_at).toLocaleString()}{p.note ? ` · ${p.note}` : ""}</p>
            </div>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${p.status === "paid" ? "bg-emerald-500/20 text-emerald-400" : p.status === "rejected" ? "bg-rose-500/20 text-rose-400" : "bg-amber-500/20 text-amber-400"}`}>
              {p.status}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
